import React, { useState, useEffect, useCallback } from 'react';
import {
  CurrencyDollarIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';
import { fetchSettlements } from '../api/marketplaceApi';

const SupplierSettlements: React.FC = () => {
  const [settlements, setSettlements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filter === 'all' ? {} : { status: filter };
      const response = await fetchSettlements(params);
      setSettlements(response.data?.items || response.data || response.items || response || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load settlements');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n: number) => new Intl.NumberFormat('en-RW', { style: 'currency', currency: 'RWF', minimumFractionDigits: 0 }).format(n || 0);

  const totalPaid = settlements.filter((s: any) => s.status === 'paid').reduce((sum: number, s: any) => sum + (s.supplier_payout || s.amount || 0), 0);
  const totalPending = settlements.filter((s: any) => s.status === 'pending').reduce((sum: number, s: any) => sum + (s.supplier_payout || s.amount || 0), 0);
  const totalCommission = settlements.reduce((sum: number, s: any) => sum + (s.platform_commission || s.commission || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <WalletIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Earnings & Settlements</h1>
              <p className="text-emerald-100 mt-1">Track your payouts and earnings history</p>
            </div>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 font-medium transition-all">
            <ArrowPathIcon className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg shadow-emerald-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Earned</p>
              <p className="text-3xl font-extrabold text-emerald-600 mt-2">{fmt(totalPaid)}</p>
              <p className="text-xs text-gray-400 mt-1">{settlements.filter((s: any) => s.status === 'paid').length} payments received</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg">
              <CheckCircleIcon className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg shadow-amber-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Pending Payout</p>
              <p className="text-3xl font-extrabold text-amber-600 mt-2">{fmt(totalPending)}</p>
              <p className="text-xs text-gray-400 mt-1">{settlements.filter((s: any) => s.status === 'pending').length} awaiting payment</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
              <ClockIcon className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg shadow-violet-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Platform Commission</p>
              <p className="text-3xl font-extrabold text-violet-600 mt-2">{fmt(totalCommission)}</p>
              <p className="text-xs text-gray-400 mt-1">deducted from earnings</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center shadow-lg">
              <ArrowTrendingUpIcon className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'pending', 'paid'].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all ${filter === f ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' : 'bg-white text-gray-600 border border-gray-200 hover:border-emerald-300'}`}>
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-emerald-200 rounded-full"></div>
            <div className="w-12 h-12 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto" />
          <p className="text-red-700 font-semibold mt-3">{error}</p>
          <button onClick={fetchData} className="mt-4 px-5 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium">Retry</button>
        </div>
      ) : settlements.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mx-auto">
            <CurrencyDollarIcon className="w-10 h-10 text-emerald-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mt-4">No settlements yet</h3>
          <p className="text-gray-500 mt-2">Earnings will appear here after orders are completed</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Settlement</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Sale Amount</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Your Payout</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {settlements.map((s: any) => (
                  <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-bold text-gray-900 text-sm">{s.settlement_number || `SET-${s.id}`}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{s.order_number || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 text-right">{fmt(s.total_sale || s.sale_amount || 0)}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-red-500 text-right">-{fmt(s.platform_commission || s.commission || 0)}</td>
                    <td className="px-6 py-4 text-sm font-extrabold text-emerald-700 text-right">{fmt(s.supplier_payout || s.amount || 0)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                        s.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        s.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-gray-100 text-gray-600 border-gray-200'
                      }`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {s.paid_at ? new Date(s.paid_at).toLocaleDateString() :
                       s.due_date ? new Date(s.due_date).toLocaleDateString() :
                       s.created_at ? new Date(s.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierSettlements;
